import { motion } from "framer-motion";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { useLanguage } from "../i18n";
import { useSEO } from "../seo";
import { PAGE_CONTENT } from "../content-pages";
import { ARTICLES } from "../articles";

const EASE = [0.22, 1, 0.36, 1];

export default function ArticlePage() {
  const { slug } = useParams();
  const { lang } = useLanguage();
  const navigate = useNavigate();
  const a = PAGE_CONTENT[lang].article;
  const article = ARTICLES.find((x) => x.slug === slug);
  useSEO(
    article ? `${article[lang].title} | OGStay Journal` : "OGStay Journal",
    article ? article[lang].excerpt : undefined
  );

  if (!article) {
    return (
      <div data-testid="article-not-found" className="flex min-h-[70vh] flex-col items-center justify-center gap-6 bg-warm px-5 pt-24 text-center">
        <h1 className="font-display text-4xl font-semibold text-navy">404</h1>
        <Link to="/guide" className="btn-dark">
          {a.back}
        </Link>
      </div>
    );
  }

  const content = article[lang];
  const related = ARTICLES.filter((x) => x.slug !== slug).slice(0, 2);

  return (
    <div data-testid="article-page" className="bg-warm">
      <article className="mx-auto max-w-4xl px-5 pb-24 pt-36 sm:px-8 sm:pt-44">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: EASE }}>
          <Link data-testid="article-back-link" to="/guide" className="group inline-flex items-center gap-2 text-xs font-extrabold uppercase tracking-[0.2em] text-mute transition-colors hover:text-navy">
            <ArrowLeft className="h-4 w-4 transition-transform duration-300 group-hover:-translate-x-1" />
            {a.back}
          </Link>
          <p className="mt-10">
            <span className="rounded-full bg-navy px-4 py-1.5 text-[11px] font-extrabold uppercase tracking-[0.15em] text-sky">{article.category[lang]}</span>
          </p>
          <h1 data-testid="article-title" className="mt-6 font-display text-3xl font-semibold leading-[1.1] tracking-tight text-navy sm:text-5xl">
            {content.title}
          </h1>
          <p className="mt-6 flex flex-wrap items-center gap-x-3 text-xs font-bold uppercase tracking-[0.15em] text-mute">
            <span>{article.author}</span>
            <span aria-hidden>·</span>
            <span>{article.date[lang]}</span>
          </p>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 0.15, ease: EASE }} className="mt-10 overflow-hidden rounded-[2rem] ring-1 ring-navy/10">
          <img src={article.image} alt={content.title} className="aspect-[16/9] w-full object-cover" />
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 0.25, ease: EASE }} className="mt-12">
          <p className="font-display text-xl italic leading-relaxed text-med sm:text-2xl">{content.excerpt}</p>
          {content.body.map((p, i) => (
            <p key={i} data-testid={`article-paragraph-${i}`} className="mt-7 text-base leading-loose text-ink/85 sm:text-lg">
              {p}
            </p>
          ))}
        </motion.div>
      </article>

      {/* Related */}
      <section className="bg-sand py-20 sm:py-24">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <h2 className="font-display text-2xl font-semibold tracking-tight text-navy sm:text-3xl">{a.related}</h2>
          <div className="mt-10 grid gap-8 sm:grid-cols-2">
            {related.map((rel, i) => (
              <Link
                key={rel.slug}
                to={`/guide/${rel.slug}`}
                data-testid={`article-related-${i}`}
                className="group flex flex-col overflow-hidden rounded-[1.75rem] bg-white ring-1 ring-navy/10 transition-[transform,box-shadow] duration-500 hover:-translate-y-1.5 hover:shadow-[0_30px_60px_-25px_rgba(15,43,58,0.3)] sm:flex-row"
              >
                <div className="aspect-[4/3] overflow-hidden sm:aspect-auto sm:w-2/5">
                  <img src={rel.image} alt={rel[lang].title} loading="lazy" className="h-full w-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.06]" />
                </div>
                <div className="flex flex-1 flex-col justify-center p-7">
                  <span className="text-[10px] font-extrabold uppercase tracking-[0.2em] text-mute">{rel.category[lang]}</span>
                  <h3 className="mt-2 font-display text-lg font-semibold leading-snug text-navy">{rel[lang].title}</h3>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section data-testid="article-cta" className="bg-navy py-20 text-center text-white sm:py-24">
        <div className="mx-auto max-w-2xl px-5">
          <h2 className="font-display text-3xl font-semibold leading-tight tracking-tight sm:text-4xl">{a.ctaTitle}</h2>
          <p className="mt-4 text-base text-white/65">{a.ctaSub}</p>
          <button data-testid="article-cta-button" onClick={() => navigate("/strutture")} className="btn-primary mt-8">
            {a.cta}
            <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </section>
    </div>
  );
}
